/**
 * Activity Feed Page
 *
 * Full page view of all user activities, grouped by month.
 */

import {
  ChevronRight,
  Activity,
  Loader2,
  Baby,
  PartyPopper,
  FileText,
  Milestone,
  CalendarDays,
  Users,
  UserMinus,
  CheckSquare,
  ListChecks,
  Heart,
  Star,
  Calendar,
  CalendarCheck,
  Sparkles,
  Trophy,
  Cake,
  Utensils,
  Droplets,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
  type LucideIcon,
} from 'lucide-react';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { useAuth } from '@/features/auth';
import { ActivityFeedEntry, ACTIVITY_TYPE_CONFIG, LucideIconName } from '../types';
import { formatActivityDate } from '../services/activityFeedService';

// Map icon names to Lucide components
const ICON_MAP: Record<LucideIconName, LucideIcon> = {
  Baby,
  PartyPopper,
  FileText,
  Milestone,
  CalendarDays,
  Users,
  UserMinus,
  CheckSquare,
  ListChecks,
  Heart,
  Star,
  Calendar,
  CalendarCheck,
  Sparkles,
  Trophy,
  Cake,
  Utensils,
  Droplets,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
};

export function ActivityFeedPage() {
  const { user } = useAuth();
  const { activities, groupedActivities, isLoading, error, hasMore, loadMore } = useActivityFeed({
    dashboardMode: false,
    limit: 20,
    autoFetch: true,
  });

  // Get user registration date for display
  const registrationDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-white text-2xl font-semibold flex items-center gap-3">
          <Activity className="w-7 h-7" />
          Deine Aktivitäten
        </h1>
        {registrationDate && (
          <p className="text-white/60 text-sm mt-1">
            Seit Registrierung: {registrationDate}
          </p>
        )}
      </div>

      {/* Loading State */}
      {isLoading && activities.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="glass-effect rounded-[15px] p-6 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && activities.length === 0 && !error && (
        <div className="glass-effect rounded-[15px] p-8 text-center">
          <Activity className="w-12 h-12 text-white/30 mx-auto mb-4" />
          <p className="text-white/60">Noch keine Aktivitäten vorhanden.</p>
          <p className="text-white/40 text-sm mt-2">
            Deine Aktivitäten werden hier angezeigt, sobald du die App nutzt.
          </p>
        </div>
      )}

      {/* Activities grouped by month */}
      {activities.length > 0 && (
        <div className="space-y-8">
          {Array.from(groupedActivities.entries()).map(([month, monthActivities]) => (
            <div key={month}>
              {/* Month Header */}
              <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-4">
                {month}
              </h2>

              {/* Activities */}
              <div className="space-y-3">
                {monthActivities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-8 text-center">
          <button
            onClick={loadMore}
            disabled={isLoading}
            className="px-8 py-3 bg-pp-orange rounded-pp text-white font-semibold disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Mehr laden'
            )}
          </button>
        </div>
      )}

      {/* End of list */}
      {!hasMore && activities.length > 0 && (
        <div className="mt-8 text-center">
          <p className="text-white/40 text-sm">Alle Aktivitäten geladen</p>
        </div>
      )}
    </div>
  );
}

interface ActivityItemProps {
  activity: ActivityFeedEntry;
}

function ActivityItem({ activity }: ActivityItemProps) {
  const config = ACTIVITY_TYPE_CONFIG[activity.activity_type] || {
    icon: 'FileText' as LucideIconName,
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: activity.activity_type,
  };

  // Get the icon component from the map
  const IconComponent = ICON_MAP[config.icon] || FileText;

  const formattedDate = formatActivityDate(activity.activity_date);

  // Check if it's an SSW activity with developments (milestone or change)
  const isSSWActivity = activity.activity_type === 'ssw_milestone' || activity.activity_type === 'ssw_change';
  const rawDevelopments = activity.metadata?.developments;

  // Parse developments - can be string[] or JSON array from database
  let developments: string[] = [];
  if (rawDevelopments) {
    if (Array.isArray(rawDevelopments)) {
      developments = rawDevelopments as string[];
    } else if (typeof rawDevelopments === 'string') {
      developments = [rawDevelopments];
    }
  }

  return (
    <div className="glass-effect rounded-[15px] p-5">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.bgColor}`}>
          <IconComponent className={`w-5 h-5 ${config.color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Date */}
          <p className="text-white/50 text-xs mb-1">{formattedDate}</p>

          {/* Title */}
          <p className="text-white font-semibold">{activity.title}</p>

          {/* Description */}
          {activity.description && (
            <p className="text-white/70 text-sm mt-1">{activity.description}</p>
          )}

          {/* SSW Activity Developments - with ChevronRight icons */}
          {isSSWActivity && developments.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-white/50 text-xs font-medium uppercase tracking-wide">Entwicklung</p>
              <ul className="space-y-1.5">
                {developments.map((dev, index) => (
                  <li key={index} className="text-white/75 text-sm flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-pp-orange flex-shrink-0 mt-0.5" />
                    <span>{dev}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actor (for partner activities) */}
          {activity.actor_display_name && activity.actor_user_id !== activity.user_id && (
            <p className="text-white/50 text-xs mt-2">
              von {activity.actor_display_name}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityFeedPage;
